import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { BarChart3, Clock } from 'lucide-react';

/**
 * Small visual building blocks for the /admin dashboard tab.
 *   - StatCard         : single big KPI tile
 *   - ActivationsChart : 7-day activation bar chart
 *   - RecentActivations: scrollable list of last 10 activations
 *
 * Extracted from AdminPanel.jsx on 28/05/2026.
 */
export function StatCard({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="bg-ink-800 rounded-xl p-4 border border-ink-600 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-slate-500">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function ActivationsChart({ data }) {
  if (!data || data.length === 0) return null;
  const last14 = data.slice(-14);
  return (
    <div className="bg-ink-800 rounded-xl p-4 border border-ink-600 shadow-sm" data-testid="activations-chart">
      <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-foreground" /> Activations (14 derniers jours)
      </h3>
      <div className="h-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={last14}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E7EB' }}
              labelFormatter={d => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
              formatter={(v) => [v, 'Activations']}
            />
            <Bar dataKey="count" fill="#1F3864" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RecentActivations({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-ink-800 rounded-xl p-4 border border-ink-600 shadow-sm" data-testid="recent-activations">
      <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-foreground" /> Activations récentes
      </h3>
      <div className="space-y-2">
        {data.map((a, i) => (
          <div key={`${a.license_key}-${a.last_activated_at || i}`} className="flex items-center justify-between text-xs border-b border-ink-600 pb-2 last:border-0">
            <div>
              <span className="font-medium text-slate-200">{a.holder_name}</span>
              <code className="ph-no-capture ml-2 text-[10px] bg-ink-700/40 px-1.5 py-0.5 rounded font-mono text-slate-500">{a.license_key}</code>
            </div>
            <span className="text-slate-500">
              {new Date(a.activated_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
